import { useState, useMemo, useContext, useEffect } from 'react'
import { FileUploader } from "react-drag-drop-files"
import moment from 'moment-timezone'
import { useConfirm } from "material-ui-confirm"
import _ from 'lodash'

import { Dialog, DialogTitle, Button, IconButton, Tooltip, TextField, Box, Typography, Stack, Switch, Collapse, List } from '@mui/material'
import { TransitionGroup } from 'react-transition-group'

import HideImageOutlinedIcon from '@mui/icons-material/HideImageOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined'
import RestoreFromTrashOutlinedIcon from '@mui/icons-material/RestoreFromTrashOutlined'
import TrashIcon from '@mui/icons-material/DeleteOutline'

import { AppContext } from '/providers/app-context'
import { PassContext } from '/components/editor/pass-context'
import { contrastColor } from '/utils/contrast-color'
import ClickToCopy from '/components/click-to-copy'
import { updateClientImage, uploadClientImage } from '/providers/templates-api'
import Warning from '/assets/images/warning.svg'
import ActionErrorDialog from '/components/action-error-dialog'
import useActionErrorDialog from '/components/use-action-error-dialog'
import appleDataSelector from '/components/editor/apple/data-selector.js'
import { urlsMatchIgnoringCacheBust } from '/components/editor/image-url'

const isDeletedImage = image => image?.deleted === true || Number(image?.deleted) === 1

const ImageSelector = ({ currentImageUrl, setImageUrl, imageType, passType, deleteAllowed, removeImageFromTemplate, setRequiredSize }) => {
  const { apiSource, apiKey, imageList, setImageList, setProcessing } = useContext(AppContext)
  const { device, model, variables } = useContext(PassContext)

  const [showFileNamePrompt, setShowFileNamePrompt] = useState(false)
  const [newFilename, setNewFilename] = useState("")
  const [dummyImage, setDummyImage] = useState()
  const [selectedImage, setSelectedImage] = useState(null)
  const [showDeletedImages, setShowDeletedImages] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [filteredImageList, setFilteredImageList] = useState([])
  const [editedImage, setEditedImage] = useState(null)
  const { actionError, errorDialogOpen, setErrorDialogOpen, showActionError } = useActionErrorDialog()

  const confirm = useConfirm()

  const style = useMemo(() => {
    const backgroundColor = device === "apple" ? appleDataSelector({ template: model, variables, path: "backgroundColor" }) : variables?.colours?.background
    const color = contrastColor({bgColor:backgroundColor})
    return { backgroundColor, color }
  }, [device, model, variables])

  const reqiredImageSize = useMemo(() => {

    let width = imageSizes[device][passType][imageType].w
    if (imageSizes[device][passType][imageType].max_w !== imageSizes[device][passType][imageType].w)
      width += "-" + imageSizes[device][passType][imageType].max_w

    let height = imageSizes[device][passType][imageType].h
    if (imageSizes[device][passType][imageType].max_h !== imageSizes[device][passType][imageType].h)
      height += "-" + imageSizes[device][passType][imageType].max_h

    return `${width} x ${height} px`
  }, [device, passType, imageType])

  useEffect(() => {
    setRequiredSize(reqiredImageSize)
  }, [reqiredImageSize])

  const requiredHeightAndWidth = useMemo(() => {
    const min_width = imageSizes[device][passType][imageType].w
    const max_width = imageSizes[device][passType][imageType].max_w
    const min_height = imageSizes[device][passType][imageType].h
    const max_height = imageSizes[device][passType][imageType].max_h

    return { min_width, max_width, min_height, max_height }
  }, [device, passType, imageType])

  const sizeFits = image => {
    if (image.width < requiredHeightAndWidth.min_width) return false
    if (image.width > requiredHeightAndWidth.max_width) return false
    if (image.height < requiredHeightAndWidth.min_height) return false
    if (image.height > requiredHeightAndWidth.max_height) return false
    return true
  }

  useEffect(() => {
    const regexp = new RegExp(searchTerm)

    const filteredList = imageList.map(i => {
      let hidden = false

      if (!showDeletedImages && isDeletedImage(i)) hidden = true
      if (searchTerm !== "" && !regexp.test(i.filename)) hidden = true
      if (i.device !== device) hidden = true
      if (i.image_type !== imageType) hidden = true

      return { ...i, hidden }
    })
    setFilteredImageList(filteredList)

  }, [imageList, requiredHeightAndWidth, showDeletedImages, searchTerm])

  useEffect(() => {
    const image = imageList.find(i => urlsMatchIgnoringCacheBust(i.s3_url, currentImageUrl))

    if (image) {
      setSelectedImage(image)
      setDummyImage(null)
    } else {
      setSelectedImage(null)
      setDummyImage(currentImageUrl)
    }
  }, [imageList, currentImageUrl, imageType])

  const deleteImage = async image => {
    setImageList(prev =>
      prev.map(i => i.id === image.id ? { ...i, deleted: 1 } : i)
    )

    await updateClientImage({ apiSource, apiKey, image: { ...image, deleted: 1 } })
    // TODO handle error
  }

  const undeleteImage = async image => {
    setImageList(prev =>
      prev.map(i => i.id === image.id ? { ...i, deleted: 0 } : i)
    )

    await updateClientImage({ apiSource, apiKey, image: { ...image, deleted: 0 } })
    // TODO handle error
  }

  const chooseImage = image => {
    console.log("Setting selected iage to ", image)
    setSelectedImage(image)
    console.log("setImageUrl", image.s3_url)
    setImageUrl(image.s3_url)
  }

  const handleImageUpload = file => {
    const filename = file.name

    const reader = new FileReader()
    reader.onloadend = async () => {

      // Wrap image dimension detection in a helper
      const getImageSize = async file => {
        return new Promise((resolve, reject) => {
          const objectUrl = URL.createObjectURL(file)
          const img = new Image()
          img.onload = () => {
            resolve({ width: img.width, height: img.height })
            URL.revokeObjectURL(objectUrl)
          }
          img.onerror = (err) => {
            reject(err)
            URL.revokeObjectURL(objectUrl)
          }
          img.src = objectUrl
        })
      }

      const checkImageSize = async (image) => {

        if (sizeFits(image)) return true

        return (await confirm({ title: "Incorrectly sized image", description: "Do you want to use the image anyway. It's size will be adjusted to fit and cropped as necessary." })).confirmed
      }

      // Get dimensions before upload
      const { width, height } = await getImageSize(file)

      if (await checkImageSize({ width, height })) {
        doUploadToS3(filename, reader.result)
      }
    }
    reader.readAsDataURL(file)
  }

  const doUploadToS3 = async (filename, imageData) => {
    const base64Data = imageData.replace(/^data:image\/\w+;base64,/, '')
    setProcessing(true)
    const result = await uploadClientImage({ apiSource, apiKey, device, imageType, passType, filename, base64Data })
    setProcessing(false)

    if (result.ok) {
      setImageList(prev => [...prev, result.image])
      chooseImage(result.image)
    } else {
      showActionError(result.error, "Error uploading image")
    }
  }

  const closeFileNamePrompt = (e, reason) => {
    if (reason && reason === "backdropClick")
      return

    setShowFileNamePrompt(false)
  }

  const saveNewFilename = async () => {
    setShowFileNamePrompt(false)

    setImageList(prev =>
      prev.map(i => i.id === editedImage.id ? { ...i, filename: newFilename } : i)
    )

    await updateClientImage({ apiSource, apiKey, image: { ...editedImage, filename: newFilename } })
    // TODO handle error
  }

  const editFileName = image => {
    setShowFileNamePrompt(true)
    setEditedImage(image)
    setNewFilename(image.filename)
  }

  const removeImage = () => {
    setSelectedImage(null)
    removeImageFromTemplate()
    setDummyImage(null)
  }

  const DropTargetContent = (
    <div style={{ borderRadius: 10, border: "2px dashed white", paddingLeft: 40, paddingRight: 40, paddingTop: 10, paddingBottom: 10, marginTop: 20, marginBottom: 20 }}>
      <div className='flex flex-col gap-2'>
        <div className='text-2xl'>Upload a new image</div>
        <Typography variant='h6' sx={{ color: "secondary.main" }}>{reqiredImageSize}</Typography>
        <div>Click to select an image, or drag an image here</div>
        <div>Accepted formats: ["png", "jpg", "jpeg"]</div>
      </div>
    </div>
  )

  return (
    <>
      {/* Left column */}
      <div className="flex flex-col md:w-1/2 flex-1 gap-2 image-selector">
        {/* Scrollable area */}
        <div className="flex-1 overflow-y-auto pr-3">
          <ImageDetails device={device} image={selectedImage} selectedImage={selectedImage} editFileName={editFileName} imageType={imageType} style={style} setSelectedImage={setSelectedImage} deleteAllowed={deleteAllowed} removeImage={removeImage} sizeFits={sizeFits} />
          <NoImageSelected dummyImage={dummyImage} selectedImage={selectedImage} imageType={imageType} style={style} deleteAllowed={deleteAllowed} removeImage={removeImage} />
          <FileUploader
            handleChange={handleImageUpload}
            types={["png", "jpg", "jpeg"]}
            children={DropTargetContent}
            dropMessageStyle={{ padding: 0, margin: 0, borderRadius: 10, color: "black", fontSize: "clamp(1.5rem, 5vw, 3rem)", whiteSpace: "nowrap" }}
          />
        </div>
      </div>

      {/* Right column */}
      <div className="flex flex-col md:w-1/2 flex-1 gap-2 image-selector">
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography>Show deleted Images : no</Typography>
          <Switch checked={showDeletedImages} onChange={e => setShowDeletedImages(e.target.checked)} />
          <Typography>yes</Typography>
        </Stack>

        <TextField
          label="Search image names"
          value={searchTerm}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={e => setSearchTerm(e.target.value)}
          fullWidth
        />

        <Typography color="secondary" variant="h6">Image library</Typography>

        {/* Scrollable area */}
        <div className="flex-1 overflow-y-scroll pr-3">
          <List disablePadding dense>
            <TransitionGroup>
              {filteredImageList.filter(image => !image.hidden).map(image =>
                <Collapse key={image.id} timeout={400} unmountOnExit>
                  <div>
                    <ImageDetails device={device} image={image} selectedImage={selectedImage} chooseImage={chooseImage} imageType={imageType} deleteImage={deleteImage} undeleteImage={undeleteImage} editFileName={editFileName} style={style} deleteAllowed={deleteAllowed} removeImage={removeImage} sizeFits={sizeFits} />
                  </div>
                </Collapse>
              )}
            </TransitionGroup>
          </List>
        </div>
      </div >

      <Dialog onClose={closeFileNamePrompt} open={showFileNamePrompt}>
        <DialogTitle>Enter a new name</DialogTitle>
        <div className="popupFieldEditor">

          <Box
            sx={{ '& .MuiTextField-root': { m: 1, width: '420px' } }}
            noValidate
            autoComplete="off"
          >
            <TextField
              value={newFilename}
              onChange={e => setNewFilename(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              type="search" />
          </Box>

          <hr className='mt-5 mb-10' />

          <div className="flex flex-row gap-5 justify-between">
            <Button onClick={closeFileNamePrompt}>Cancel</Button>
            <Button onClick={saveNewFilename} disabled={newFilename === editedImage?.filename || newFilename.length < 3}>Save</Button>
          </div>
        </div>
      </Dialog>
      <ActionErrorDialog title="Image action failed" open={errorDialogOpen} setOpen={setErrorDialogOpen} error={actionError} />
    </>
  )
}

export default ImageSelector

const ImageDetails = ({ device, image, editFileName, selectedImage, chooseImage, style, imageType, removeImage, deleteAllowed, deleteImage, undeleteImage, sizeFits }) => {
  const imageStyle = useMemo(() => {
    return imageType === "icon" ? { backgroundColor: "white", color: "black" } : style
  }, [imageType, style])

  const buttonContrastColour = useMemo(() => {
    return contrastColor({ bgColor: imageStyle.backgroundColor })
  }, [imageStyle])

  const isGoogleLogo = device === "google" && imageType === "logo"
  const clipperClass = isGoogleLogo ? "imageSelectorLogoClipper" : ""
  const selectedImageId = selectedImage?.id
  const selectedImageUrl = selectedImage?.s3_url
  const isSelected = Boolean(
    image && selectedImage && (
      (image.id && selectedImageId && image.id === selectedImageId) ||
      (image.s3_url && selectedImageUrl && urlsMatchIgnoringCacheBust(image.s3_url, selectedImageUrl))
    )
  )

  const DeleteIcon = ({ image }) => {
    if (!isSelected) {

      if (!isDeletedImage(image)) return (
        <div className='bg-white p-1 m-0 rounded-full border-2 border-red-600'>
          <Tooltip title="Delete image"><IconButton onClick={() => deleteImage(image)}><TrashIcon sx={{ fontSize: 30 }} className="text-red-600" /></IconButton></Tooltip>
        </div>
      )
      else return (
        <div className='bg-white p-1 m-0 rounded-full border-2 border-green-600'>
          <Tooltip title="Recover deleted image"><IconButton onClick={() => undeleteImage(image)}><RestoreFromTrashOutlinedIcon sx={{ fontSize: 30 }} className="text-green-500" /></IconButton></Tooltip>
        </div>
      )
    } else return (
      <div className='p-1 m-0 rounded-full border-2 opacity-0'>
        <IconButton><CheckCircleOutlineOutlinedIcon sx={{ fontSize: 30 }} /></IconButton>
      </div>
    )
  }

  const SelectIcon = ({ image }) => {
    if (!isSelected && !isDeletedImage(image)) return (
      <div className='bg-white p-1 m-0 rounded-full border-2 border-green-600'>
        <Tooltip title="Select image"><IconButton onClick={() => chooseImage(image)}><CheckCircleOutlineOutlinedIcon sx={{ fontSize: 30 }} className="text-green-500" /></IconButton></Tooltip>
      </div>
    )
  }

  if (image) {
    const fits = sizeFits(image)
    const classes = 'relative flex flex-col w-full gap-2 items-center relative' + (isSelected ? "" : " selectable-image m-2")
    const dimDeletedClass = isDeletedImage(image) ? "flex flex-col items-center w-full bg-black/25" : "flex flex-col items-center w-full"
    //    const sizeFitsClass = fits ? "" : "text-red-600"


    return (
      <>
        {isSelected && <Typography variant='h4' sx={{ textAlign: "center", marginBottom: 5 }}>Selected Image</Typography>}
        <div style={imageStyle} className={classes}>
          <div className={dimDeletedClass}>
            <div className='flex flex-col gap-2 items-center'>
              <div>{image.filename}</div>
              <Button onClick={() => editFileName(image)} color={buttonContrastColour}><EditOutlinedIcon className='mr-2' />Edit name</Button>
              <div className='flex flex-row gap-2'>
                {!fits && <img src={Warning} className='-mt-1 mb-1' />}
                <div className='text-nowrap'>{image.width} x {image.height} px</div>
                {!fits && <div >(Incorrect size)</div>}
              </div>
              <div className='text-nowrap'>Uploaded: {moment(image.uploaded_date).format("DD MMM YYYY")}</div>
            </div>

            <div className={clipperClass}>
              <img src={image?.s3_url} />
            </div>

            {image.s3_url.substring(0, 4) === "http" && (
              <div className='text-xs p-3'>
                <ClickToCopy source={image.s3_url} successMessage="Image URL copied">
                  Copy Image URL
                </ClickToCopy>
              </div>
            )}

            {(isSelected && deleteAllowed) &&
              <Button onClick={removeImage} color={buttonContrastColour}><HideImageOutlinedIcon className='mr-2' />Remove Image</Button>}
          </div>
          {!isSelected && <>
            <div className='p-3 absolute top-1 left-1'><SelectIcon image={image} /></div>
            <div className='p-3 absolute top-1 -right-1'><DeleteIcon image={image} /></div>
          </>}
        </div>
      </>
    )
  }
}


const NoImageSelected = ({ dummyImage, selectedImage, imageType, style, deleteAllowed, removeImage }) => {

  const buttonColour = useMemo(() => {
    return contrastColor({ bgColor: imageType === "icon" ? "#ffffff" : style.backgroundColor })
  }, [imageType])

  const placeHolderImage = dummyImage && dummyImage !== `{{images.google.${imageType}}}`

  if (!selectedImage) return (
    <>
      <Typography variant='h4' sx={{ textAlign: "center", marginBottom: 2 }}>{placeHolderImage ? "Placeholder" : "No Selected Image"}</Typography>

      <div style={imageType === "icon" ? { backgroundColor: "white", color: "black" } : style} className='flex flex-col gap-5 p-10 items-center'>
        {!placeHolderImage && <HideImageOutlinedIcon style={{ fontSize: 200 }} />}
        {placeHolderImage && <img src={dummyImage} />}
        <div className='text-xl'>{placeHolderImage ? "Replace this with your own image" : "No image selected"}</div>
        <div className='text-l'>Choose or upload an image</div>
        {placeHolderImage && deleteAllowed && <Button onClick={removeImage} color={buttonColour}><HideImageOutlinedIcon className='mr-2' />Remove Image</Button>}
      </div>
    </>
  )
}

const appleSizes = {
  icon: { w: 87, max_w: 87, h: 87, max_h: 87, aspect: 1 },
  logo: { w: 150, max_w: 780, h: 150, max_h: 150 },
  thumbnail: { w: 320, max_w: 320, h: 320, max_h: 320, aspect: 1 },
  background: { w: 180, max_w: 180, h: 220, max_h: 220, aspect: 180 / 220, },
}

const googleSizes = {
  logo: { w: 660, max_w: 660, h: 660, max_h: 660, aspect: 1 },
  "wideLogo": { w: 1280, max_w: 1280, h: 400, max_h: 400 },
  hero: { w: 1032, max_w: 1032, h: 200, max_h: 1000 },
  stampcard: { w: 1032, max_w: 1032, h: 336, max_h: 660 },
}

const imageSizes = {
  apple: {
    coupon: {
      ...appleSizes,
      strip: { w: 1125, max_w: 1125, h: 432, max_h: 432, aspect: 1125 / 432 },
    },
    eventTicket: {
      ...appleSizes,
      strip: { w: 1125, max_w: 1125, h: 294, max_h: 294, aspect: 1125 / 294 },
    },
    storeCard: {
      ...appleSizes,
      strip: { w: 1125, max_w: 1125, h: 432, max_h: 432, aspect: 1125 / 432 },
      stampcard: { w: 1125, max_w: 1125, h: 432, max_h: 432, aspect: 1125 / 432 },
    },
    boardingPass: appleSizes,
    generic: appleSizes,
    key: appleSizes
  },
  google: {
    generic: googleSizes,
    offer: googleSizes,
    transit: googleSizes,
    eventTicket: googleSizes,
    giftCard: googleSizes,
    access: googleSizes,
    loyalty: googleSizes
  }
}
